/**
 * PhoneGate — app-shell gate that requires every user to have a mobile number
 * on file (TMAR-613). Rendered once from <Main>, next to UpgradeModal /
 * ArchivedModal.
 *
 * Non-admins are hard-blocked until they save a valid number. Admins/owners get
 * the same modal with a "Remind me later" that snoozes 24h, so they can't be
 * locked out of billing/settings.
 *
 * Redux `userDetails` is persisted (redux-persist, whole store) and there's no
 * boot-time refresh of the current user, so a user who actually has a phone can
 * have a stale local copy without one. We confirm against the server before
 * gating and FAIL OPEN on any error — a flaky network must never lock anyone out.
 *
 * The caller (<Main>) already suppresses this for fullscreen/kiosk routes and
 * when the archived / upgrade modals are up.
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { UseUserDetails, UseIsDemo } from "../../hooks/auth";
import { UseViewAsUser } from "../../hooks/user";
import { updateUserDetails } from "../../redux/auth/actions";
import { GET_USER } from "../../api/user";
import { getPageProperties } from "../../routes/routes";
import PhoneCaptureModal from "./PhoneCaptureModal";

// Routes where the gate must never appear even for a beat: the TV/kiosk
// leaderboard, and the payment/upgrade screens (blocking someone mid-payment
// would be the worst possible time). Checked here directly off the current
// path — synchronous, no dependency on <Main> resolving its page props first.
const isBlockedRoute = (pathname) => {
  if (pathname === "/signout" || pathname.startsWith("/live/")) return true;
  const props = getPageProperties({ path: pathname });
  return Boolean(props?.hideSideNav && props?.hideTopbar);
};

// A phone "counts" if the field holds anything non-empty — any non-empty
// string, or any truthy non-string (legacy records occasionally store the
// number as a Number). Deliberately lenient: the goal is to require a phone
// from people who have *none*, not to police the format of existing data,
// and never to risk gating someone who already put something there.
// Never calls String methods on an unknown type — a throw here would
// white-screen the whole app for that user, which is worse than any gate.
const hasPhoneOnFile = (value) =>
  typeof value === "string" ? value.length > 0 : Boolean(value);

const SNOOZE_KEY = "gmb.phoneGate.snoozedAt";
const SNOOZE_MS = 24 * 60 * 60 * 1000; // 24h

function readSnoozeAt() {
  try {
    const raw = window.localStorage.getItem(SNOOZE_KEY);
    const ts = raw ? Number(raw) : 0;
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
}

function writeSnoozeNow() {
  try {
    window.localStorage.setItem(SNOOZE_KEY, String(Date.now()));
  } catch {
    /* private mode / disabled storage — user just sees the modal next load */
  }
}

export default function PhoneGate() {
  const user = UseUserDetails();
  const isDemo = UseIsDemo();
  const viewAs = UseViewAsUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // "loading" until the server check resolves; then "has" | "missing" | "error".
  const [serverState, setServerState] = useState("loading");
  const [snoozedAt, setSnoozedAt] = useState(readSnoozeAt);

  const userId = user?._id;

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    GET_USER(userId)
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.phoneExempt) {
          setServerState("has");
          return;
        }
        if (hasPhoneOnFile(data?.phoneNumber)) {
          // Heal stale persisted state so other screens stop nagging too.
          if (!hasPhoneOnFile(user?.phoneNumber)) {
            dispatch(updateUserDetails({ phoneNumber: data.phoneNumber }));
          }
          setServerState("has");
          return;
        }
        setServerState("missing");
      })
      .catch(() => {
        if (!cancelled) setServerState("error");
      });

    return () => {
      cancelled = true;
    };
    // Only re-run when the user identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Exemptions the shell doesn't already cover.
  if (!userId || isDemo || viewAs || user?.phoneExempt) return null;
  // Never gate on a kiosk / payment / sign-out route (see isBlockedRoute).
  if (isBlockedRoute(location.pathname)) return null;
  // Fast path when redux is fresh.
  if (hasPhoneOnFile(user?.phoneNumber)) return null;
  // Only gate once the server confirms the number is really missing.
  if (serverState !== "missing") return null;

  const isAdminTier = Boolean(user?.isAdmin);
  if (isAdminTier && Date.now() - snoozedAt < SNOOZE_MS) return null;

  return (
    <PhoneCaptureModal
      open
      required
      allowRemindLater={isAdminTier}
      onRemindLater={() => {
        writeSnoozeNow();
        setSnoozedAt(Date.now());
      }}
      onSignOut={() => navigate("/signout")}
    />
  );
}
